import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { reportAPI, exportAPI, offlineSaleAPI } from '../services/api';
import { StatCard, Badge, Spinner, PageHeader } from '../components/Common';


const COLORS = ['#6c63ff','#00d4aa','#ffa94d','#ff6b6b','#a29bfe','#00b894'];

const ReportsPage = () => {
  const [stockData, setStockData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [period,    setPeriod]    = useState('month');
  const [loading,   setLoading]   = useState(true);
  const [exporting, setExporting] = useState(false);
  const [offlineSales, setOfflineSales] = useState([]);
  const [offlineTotal, setOfflineTotal] = useState(0);

  const handleExport = async () => {
  setExporting(true);
  try {
    const res = await exportAPI.salesExcel(period);
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Bhamsa-Report-' + new Date().toISOString().split('T')[0] + '.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Excel download ho gaya! ✅');
  } catch {
    toast.error('Export nahi hua');
  } finally { setExporting(false); }
};
useEffect(() => {
  setLoading(true);
  Promise.all([reportAPI.getStock(), reportAPI.getSales(period)])
    .then(([s1, s2]) => { setStockData(s1.data); setSalesData(s2.data); })
    .catch(() => toast.error('Reports load nahi hue'))
    .finally(() => setLoading(false));
  offlineSaleAPI.getAll().then(r => {
    setOfflineSales(r.data.sales || []);
    setOfflineTotal(r.data.total || 0);
  }).catch(() => {});
}, [period]);

  if (loading) return <Spinner />;

  const { products = [], summary = {} } = stockData || {};
  const { invoices = [] } = salesData || {};

  // Category pie chart data
  const catMap = {};
  products.forEach(p => {
    catMap[p.category] = (catMap[p.category] || 0) + p.stock_value_sell;
  });
  const catData = Object.entries(catMap).map(([name, value]) => ({ name, value }));

  // Margin bar chart
  const marginData = [...products]
    .sort((a, b) => b.profit_margin_pct - a.profit_margin_pct)
    .slice(0, 8);

  return (
    <div className="page">
      <PageHeader
        title="📈 Reports & Analytics"
        actions={
          <div style={{ display:'flex', gap:6 }}>
            {['week','month','year'].map(p => (
              <button key={p} className={'btn btn-sm ' + (period===p ? 'btn-primary' : 'btn-ghost')} onClick={() => setPeriod(p)}>
                {p==='week'?'7 Days':p==='month'?'30 Days':'1 Year'}
              </button>
            ))}
            <button 
              className="btn btn-success btn-sm" 
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? '⏳ Exporting...' : '📥 Excel Export'}
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="stats-grid gstr-grid">


          {/* Stock Buy Value */}

          <div className="stats-container">
            <div className="stat-card stat-blue">
              <div className="icon">💹</div>
              <div className="details">
                <div className="value">
                  ₹{(summary.total_buy_value || 0).toLocaleString('en-IN')}
                </div>
                <div className="label">Stock Buy Value</div>
                <div className="sub-text">
                  Purchase price
                </div>
              </div>
            </div>

            {/* Stock Sell Value */}
            <div className="stat-card stat-green">
              <div className="icon">💰</div>
              <div className="details">
                <div className="value">
                  ₹{(summary.total_sell_value || 0).toLocaleString('en-IN')}
                </div>
                <div className="label">Stock Sell Value</div>
                <div className="sub-text">
                  Selling price
                </div>
              </div>
            </div>

            {/* Potential Profit */}
            <div className="stat-card stat-orange">
              <div className="icon">📈</div>
              <div className="details">
                <div className="value">
                  ₹{(summary.potential_profit || 0).toLocaleString('en-IN')}
                </div>
                <div className="label">Potential Profit</div>
                <div className="sub-text">
                  Agar sab bik jaye
                </div>
              </div>
            </div>

            {/* Period Revenue */}
            <div className="stat-card stat-blue">
              <div className="icon">📊</div>
              <div className="details">
                <div className="value">
                  ₹{(salesData?.total_revenue || 0).toLocaleString('en-IN')}
                </div>
                <div className="label">Period Revenue</div>
                <div className="sub-text">
                  {salesData?.total_invoices || 0} invoices
                </div>
              </div>
            </div>
          </div>

        {/* <StatCard icon="💹" label="Stock Buy Value"
          value={`₹${(summary.total_buy_value||0).toLocaleString('en-IN')}`}
          color="blue" sub="Purchase price" />
        <StatCard icon="💰" label="Stock Sell Value"
          value={`₹${(summary.total_sell_value||0).toLocaleString('en-IN')}`}
          color="green" sub="Selling price" />
        <StatCard icon="📈" label="Potential Profit"
          value={`₹${(summary.potential_profit||0).toLocaleString('en-IN')}`}
          color="orange" sub="Agar sab bik jaye" />
        <StatCard icon="📊" label="Period Revenue"
          value={`₹${(salesData?.total_revenue||0).toLocaleString('en-IN')}`}
          color="blue" sub={`${salesData?.total_invoices||0} invoices`} /> */}
      </div>

      <div className="two-col">
        {/* Pie Chart */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">🥧 Category-wise Stock</span>
          </div>
          <div className="card-body" style={{ height:280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={catData} cx="50%" cy="50%"
                  outerRadius={90} dataKey="value"
                  label={({ name, percent }) =>
                    `${name} ${(percent*100).toFixed(0)}%`}
                  labelLine={false} fontSize={11}
                >
                  {catData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={v => `₹${v.toLocaleString('en-IN')}`}
                  contentStyle={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bar Chart */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">📊 Product Margins (%)</span>
          </div>
          <div className="card-body" style={{ height:280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={marginData}
                margin={{ top:5, right:10, bottom:30, left:0 }}>
                <XAxis dataKey="name"
                  tick={{ fill:'var(--text3)', fontSize:10 }}
                  angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fill:'var(--text3)', fontSize:11 }} />
                <Tooltip
                  formatter={v => `${v}%`}
                  contentStyle={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8 }}
                />
                <Bar dataKey="profit_margin_pct" fill="var(--accent)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Stock Table */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">📦 Stock Report</span>
          <div style={{ display:'flex', gap:8 }}>
            <Badge variant="red">🔴 {summary.out_of_stock_count} Out</Badge>
            <Badge variant="orange">🟡 {summary.low_stock_count} Low</Badge>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Product</th><th>Category</th><th>Stock</th>
                <th>Buy Value</th><th>Sell Value</th><th>Margin</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id}>
                  <td><strong>{p.name}</strong></td>
                  <td>{p.category}</td>
                  <td>{p.quantity} {p.unit}</td>
                  <td style={{ color:'var(--text2)' }}>
                    ₹{p.stock_value_buy.toLocaleString('en-IN')}
                  </td>
                  <td style={{ color:'var(--accent2)' }}>
                    ₹{p.stock_value_sell.toLocaleString('en-IN')}
                  </td>
                  <td>
                    <Badge variant={p.profit_margin_pct>=30?'green':p.profit_margin_pct>=15?'orange':'red'}>
                      {p.profit_margin_pct}%
                    </Badge>
                  </td>
                  <td>
                    <Badge variant={p.status==='in_stock'?'green':p.status==='low_stock'?'orange':'red'}>
                      {p.status==='in_stock'?'In Stock':p.status==='low_stock'?'Low Stock':'Out of Stock'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Offline Sales Table */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">⚡ Offline Sales</span>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:13, color:'var(--accent2)', fontWeight:600 }}>
              Total: ₹{parseFloat(offlineTotal).toLocaleString('en-IN')}
            </span>
            <Badge variant="blue">{offlineSales.length} sales</Badge>
          </div>
        </div>
        <div className="table-wrap">
          {offlineSales.length === 0 ? (
            <div style={{ padding:24, textAlign:'center', color:'var(--text3)' }}>
              Koi offline sales nahi
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Total</th>
                  <th>Note</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {offlineSales.map(s => (
                  <tr key={s.id}>
                    <td><strong>{s.product_name}</strong></td>
                    <td>{s.quantity} {s.unit}</td>
                    <td>₹{parseFloat(s.unit_price).toFixed(0)}</td>
                    <td style={{ color:'var(--accent2)', fontWeight:600 }}>
                      ₹{parseFloat(s.total_price).toLocaleString('en-IN')}
                    </td>
                    <td style={{ color:'var(--text3)', fontSize:12 }}>{s.note || '—'}</td>
                    <td style={{ color:'var(--text2)', fontSize:12 }}>
                      {new Date(s.createdAt).toLocaleDateString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;